import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import {
  ReactiveFormsModule,
  FormGroup,
  FormControl,
  Validators,
} from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { HighchartsChartModule } from 'highcharts-angular';
import Highcharts from 'highcharts';
import { DataTableComponent } from '../shared/components/data-table/data-table.component';
import { FabricDataService } from '../fabric-data.service';
import { UtilService } from '../services/util.service';
import { FabricApiResponse, TableData } from '../models/api.models';
import { catchError, of, Observable } from 'rxjs';

interface TableConfig {
  id: number;
  name: string;
  graphType?: string;
  requiresDate?: boolean;
  isChart?: boolean;
  isMultiTable?: boolean;
  subTables?: Array<{
    name: string;
    graphType: string;
    requiresDate?: boolean;
  }>;
}

@Component({
  selector: 'app-ctm-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatDatepickerModule,
    MatNativeDateModule,
    ReactiveFormsModule,
    MatInputModule,
    MatButtonModule,
    MatFormField,
    MatLabel,
    MatIconModule,
    HighchartsChartModule,
    DataTableComponent,
  ],
  templateUrl: './ctm-dashboard.component.html',
  styleUrls: ['./ctm-dashboard.component.scss'],
})
export class CtmDashboardComponent implements OnInit {
  // Highcharts
  Highcharts: typeof Highcharts = Highcharts;
  chartConstructor = 'chart';
  updateFlag = false;

  selectedTable = 1;
  isTableLoading = false;
  currentTableData: TableData | null = null;
  currentChartOptions: Highcharts.Options = {};
  isChartView = false;
  multiTableData: Array<{
    name: string;
    data: TableData | null;
    isLoading: boolean;
  }> = [];

  // Date range
  fromDate = '';
  toDate = '';

  // Date range picker
  range = new FormGroup({
    start: new FormControl<Date | null>(null, Validators.required),
    end: new FormControl<Date | null>(null, Validators.required),
  });

  // Table configurations
  tableConfigs: TableConfig[] = [
    { id: 1, name: 'Raw data', graphType: 'raw_data_ms', requiresDate: true },
    {
      id: 2,
      name: 'Commissions MonthlyView',
      graphType: 'monthlyview_commision',
      requiresDate: true,
    },
    {
      id: 3,
      name: 'Monthly Sales',
      graphType: 'monthlySales_matrix',
      requiresDate: true,
      isChart: true,
    },
    {
      id: 4,
      name: 'Monthly switch report Fin',
      isMultiTable: true,
      subTables: [
        {
          name: 'MS Matrix Fin',
          graphType: 'ms_matrix_fin',
          requiresDate: false,
        },
        {
          name: 'Table Monthly Fin',
          graphType: 'table_monhtly_fin',
          requiresDate: false,
        },
      ],
    },
  ];

  constructor(
    private fabricDataService: FabricDataService,
    private util: UtilService,
  ) {
    Highcharts.setOptions({ credits: { enabled: false } });
  }

  ngOnInit(): void {
    this.prepareDateRange();
    this.loadTableData(this.selectedTable);
  }

  prepareDateRange(): void {
    const dateRange = this.util.getMonthlyDateRange();
    this.fromDate = dateRange.fromDate;
    this.toDate = dateRange.toDate;
    this.range.setValue({
      start: this.util.parseDateString(this.fromDate),
      end: this.util.parseDateString(this.toDate),
    });
  }

  applyDateChange(): void {
    if (this.range.valid && this.range.value.start && this.range.value.end) {
      this.fromDate = this.util.formatDate(this.range.value.start);
      this.toDate = this.util.formatDate(this.range.value.end);
      this.loadTableData(this.selectedTable);
    }
  }

  selectTable(tableNumber: number): void {
    this.selectedTable = tableNumber;
    this.loadTableData(tableNumber);
  }

  getTableConfig(tableId: number): TableConfig | undefined {
    return this.tableConfigs.find((config) => config.id === tableId);
  }

  getCurrentTableTitle(): string {
    const config = this.getTableConfig(this.selectedTable);
    if (config?.isMultiTable) {
      return config.name;
    }
    if (config?.isChart) {
      return config.name;
    }
    if (this.currentTableData?.label) {
      return this.currentTableData.label;
    }
    return config?.name || `Table ${this.selectedTable}`;
  }

  isMultiTableView(): boolean {
    const config = this.getTableConfig(this.selectedTable);
    return config?.isMultiTable || false;
  }

  /**
   * Transform CTM API response (object array + columns) into TableData (headers + rows).
   */
  private transformApiResponse(apiData: any): TableData {
    const columns: string[] =
      apiData.columns || Object.keys(apiData.data?.[0] || {});
    const rows = (apiData.data || []).map((row: any) =>
      columns.map((col) => row[col] ?? ''),
    );

    return {
      chartType: apiData.chartType || 'table',
      label: apiData.title || '',
      headers: columns,
      rows,
    };
  }

  loadTableData(tableId: number): void {
    const config = this.getTableConfig(tableId);
    if (!config) {
      console.error('Table configuration not found for ID:', tableId);
      return;
    }

    // Handle multi-table view
    if (config.isMultiTable && config.subTables) {
      this.isChartView = false;
      this.loadMultiTableData(config.subTables);
      return;
    }

    if (!config.graphType) {
      console.error('Graph type not found for table:', config.name);
      return;
    }

    // Handle chart view
    if (config.isChart) {
      this.loadChartData(config);
      return;
    }

    // Handle table view
    this.isChartView = false;
    this.isTableLoading = true;
    this.currentTableData = null;
    this.multiTableData = [];

    const request$: Observable<FabricApiResponse<TableData>> =
      config.requiresDate
        ? this.fabricDataService.fetchFabricTableWithDate(
            config.graphType,
            this.fromDate,
            this.toDate,
          )
        : this.fabricDataService.fetchFabricTable(config.graphType);

    request$
      .pipe(
        catchError((error) => {
          console.error(`Error fetching table data for ${config.name}:`, error);
          this.util.openSnackBar(
            `Error fetching ${config.name} data`,
            'Close',
            'error',
          );
          return of({ result: { data: null } });
        }),
      )
      .subscribe({
        next: (response) => {
          if (response.result?.data) {
            this.currentTableData = this.transformApiResponse(
              response.result.data,
            );
          }
          this.isTableLoading = false;
        },
        error: (error) => {
          console.error('Unexpected error in loadTableData:', error);
          this.isTableLoading = false;
        },
      });
  }

  /**
   * Load chart data for chart-type entries (e.g. Monthly Sales)
   */
  private loadChartData(config: TableConfig): void {
    this.isChartView = true;
    this.isTableLoading = true;
    this.currentTableData = null;
    this.currentChartOptions = {};
    this.multiTableData = [];
    this.updateFlag = false;

    const request$ = config.requiresDate
      ? this.fabricDataService.fetchFabricChartWithDate(
          config.graphType!,
          this.fromDate,
          this.toDate,
        )
      : (this.fabricDataService.fetchFabricData(
          config.graphType!,
        ) as Observable<any>);

    request$
      .pipe(
        catchError((error) => {
          console.error(`Error fetching chart data for ${config.name}:`, error);
          this.util.openSnackBar(
            `Error fetching ${config.name} data`,
            'Close',
            'error',
          );
          return of({ result: { data: null } });
        }),
      )
      .subscribe({
        next: (response: any) => {
          if (response.result?.data) {
            const chartData = response.result.data;
            Object.assign(this.currentChartOptions, chartData);
            this.currentChartOptions.chart = {
              ...this.currentChartOptions.chart,
              backgroundColor: '#f7fafa',
            };
            this.updateFlag = true;
          }
          this.isTableLoading = false;
        },
        error: (error) => {
          console.error('Unexpected error in loadChartData:', error);
          this.isTableLoading = false;
        },
      });
  }

  private loadMultiTableData(
    subTables: Array<{
      name: string;
      graphType: string;
      requiresDate?: boolean;
    }>,
  ): void {
    this.currentTableData = null;
    this.isTableLoading = true;

    this.multiTableData = subTables.map((table) => ({
      name: table.name,
      data: null,
      isLoading: true,
    }));

    subTables.forEach((table, index) => {
      const request$: Observable<FabricApiResponse<TableData>> =
        table.requiresDate
          ? this.fabricDataService.fetchFabricTableWithDate(
              table.graphType,
              this.fromDate,
              this.toDate,
            )
          : this.fabricDataService.fetchFabricTable(table.graphType);

      request$
        .pipe(
          catchError((error) => {
            console.error(
              `Error fetching table data for ${table.name}:`,
              error,
            );
            return of({ result: { data: null } });
          }),
        )
        .subscribe({
          next: (response) => {
            if (response.result?.data) {
              this.multiTableData[index].data = this.transformApiResponse(
                response.result.data,
              );
            }
            this.multiTableData[index].isLoading = false;

            if (this.multiTableData.every((t) => !t.isLoading)) {
              this.isTableLoading = false;
            }
          },
          error: (error) => {
            console.error('Unexpected error loading multi-table data:', error);
            this.multiTableData[index].isLoading = false;

            if (this.multiTableData.every((t) => !t.isLoading)) {
              this.isTableLoading = false;
            }
          },
        });
    });
  }
}
